import { useState } from "react";
import { useTranslation } from "react-i18next";

const FORM_URL = "https://readdy.ai/api/form/d7idjhmhdlg6dog9dcn0";

const SUBJECTS_AZ = [
  "Hesab Problemi",
  "Texniki Dəstək",
  "FİN Doğrulama Problemi",
  "Şifrə Sıfırlama",
  "Email Konfiqurasiyası",
  "Digər",
];

export default function ContactForm() {
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    setLoading(true);
    try {
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSuccess(true);
      form.reset();
      setCharCount(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3">
            <span className="text-red-600 text-xs font-bold tracking-widest uppercase">Əlaqə</span>
            <h2 className="text-2xl font-extrabold text-gray-900 mt-2 mb-6">{t("support.form_title")}</h2>

            {success ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-green-600 text-xl flex-shrink-0 mt-0.5" />
                <p className="text-green-800 text-sm">{t("support.form_success")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4" id="support-contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">{t("support.form_name")}</label>
                    <input
                      type="text"
                      name="fullname"
                      required
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">{t("support.form_email")}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">{t("support.form_subject")}</label>
                  <select
                    name="subject"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-red-500 transition-colors bg-white text-gray-700 cursor-pointer"
                  >
                    <option value="">— Seçin —</option>
                    {SUBJECTS_AZ.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 flex justify-between">
                    <span>{t("support.form_message")}</span>
                    <span className={charCount > MAX_CHARS ? "text-red-500" : "text-gray-400"}>{charCount}/{MAX_CHARS}</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={MAX_CHARS}
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-red-500 transition-colors resize-none"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || charCount > MAX_CHARS}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold px-8 py-3 rounded-lg text-sm cursor-pointer transition-all whitespace-nowrap inline-flex items-center gap-2"
                >
                  {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                  {t("support.form_btn")}
                </button>
              </form>
            )}
          </div>

          {/* Office Info */}
          <div className="lg:col-span-2">
            <div className="bg-stone-50 rounded-2xl p-7 h-full">
              <h3 className="font-bold text-gray-900 mb-5">{t("support.office_title")}</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-red-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Ünvan</div>
                    <div className="text-sm text-gray-700">{t("support.office_addr")}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">İş Saatları</div>
                    <div className="text-sm text-gray-700">{t("support.office_hours")}</div>
                    <div className="text-sm text-gray-700">{t("support.office_weekend")}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Telefon</div>
                    <div className="text-sm text-gray-700">+994 12 000 00 00</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200">
                <div className="text-xs text-gray-400 mb-3">Sosial Media</div>
                <div className="flex gap-3">
                  {["ri-facebook-line", "ri-instagram-line", "ri-linkedin-line"].map((icon) => (
                    <a
                      key={icon}
                      href="#"
                      rel="nofollow"
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-white text-gray-600 hover:bg-red-600 hover:text-white cursor-pointer transition-all"
                    >
                      <i className={icon} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
